'use client'

import { useMemo } from 'react'

import { Tag } from '@/components/ui/tag'
import { ContentCard } from '@/components/portal/content-card'
import { DataList } from '@/components/portal/data-list'
import type { FilterDef } from '@/components/portal/search-filter-bar'
import { useListQueryState } from '@/lib/use-list-query-state'

type Partner = { id: string; name: string; type: string; typeLabel: string; tagline: string }

// Placeholder data — real partners + facet counts come from mp-core later.
const partners: Partner[] = [
  {
    id: 'omni-ftl',
    name: 'Omni Fort Lauderdale Hotel',
    type: 'hotel',
    typeLabel: 'Hotel/Resort',
    tagline: 'Designed for Coastal Indulgence.',
  },
  {
    id: 'amawaterways',
    name: 'AmaWaterways',
    type: 'cruise',
    typeLabel: 'Cruise',
    tagline: 'Journeys That Move You.',
  },
  {
    id: 'crystal',
    name: 'Crystal Cruises',
    type: 'cruise',
    typeLabel: 'Cruise',
    tagline: 'Exceptional at Sea.',
  },
  {
    id: 'st-pancras',
    name: 'St. Pancras London',
    type: 'hotel',
    typeLabel: 'Hotel/Resort',
    tagline: 'Masterpiece by Design. Dynamic by Nature.',
  },
  {
    id: 'amtrak',
    name: 'Amtrak',
    type: 'ground-transportation',
    typeLabel: 'Ground Transportation',
    tagline: 'See more. Do more.',
  },
  {
    id: 'omni-la-costa',
    name: 'Omni La Costa Resort & Spa',
    type: 'hotel',
    typeLabel: 'Hotel/Resort',
    tagline: 'California coastal calm.',
  },
  {
    id: 'emerald',
    name: 'Emerald Cruises',
    type: 'cruise',
    typeLabel: 'Cruise',
    tagline: 'Discover the world by river.',
  },
  {
    id: 'avis',
    name: 'Avis',
    type: 'ground-transportation',
    typeLabel: 'Ground Transportation',
    tagline: 'We try harder.',
  },
]

const filterDefs: FilterDef[] = [
  {
    id: 'type',
    label: 'Type',
    options: [
      { value: 'all', label: 'All' },
      { value: 'hotel', label: 'Hotel/Resort' },
      { value: 'dmc', label: 'DMC' },
      { value: 'tour-operator', label: 'Tour Operator' },
      { value: 'cruise', label: 'Cruise' },
      { value: 'air', label: 'Air' },
      { value: 'business-services', label: 'Business Services' },
      { value: 'ground-transportation', label: 'Ground Transportation' },
      { value: 'insurance', label: 'Insurance' },
    ],
  },
  {
    id: 'country',
    label: 'Country',
    multiple: true,
    options: [
      { value: 'argentina', label: 'Argentina', count: 3 },
      { value: 'australia', label: 'Australia', count: 6 },
      { value: 'austria', label: 'Austria', count: 3 },
      { value: 'belgium', label: 'Belgium', count: 5 },
      { value: 'canada', label: 'Canada', count: 8 },
      { value: 'united-states', label: 'United States', count: 24 },
    ],
  },
  {
    id: 'destinations',
    label: 'Destinations',
    multiple: true,
    options: [
      { value: 'caribbean', label: 'Caribbean', count: 12 },
      { value: 'europe', label: 'Europe', count: 20 },
      { value: 'north-america', label: 'North America', count: 15 },
      { value: 'asia', label: 'Asia', count: 9 },
    ],
  },
]
const FILTER_IDS = filterDefs.map((f) => f.id)

const PAGE_SIZE = 4

export function PartnersBrowser() {
  const { state, page, setFilterState, setPage } = useListQueryState(FILTER_IDS)

  // DEMO ONLY: in production mp-core filters/paginates and returns the page +
  // facet counts. This local filter just exercises the UI with placeholder data.
  const filtered = useMemo(() => {
    const q = state.search.trim().toLowerCase()
    const type = state.filters.type?.[0]
    return partners.filter((p) => {
      if (q && !p.name.toLowerCase().includes(q)) return false
      if (type && type !== 'all' && p.type !== type) return false
      return true
    })
  }, [state])

  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE))
  const current = Math.min(page, pageCount)
  const pageItems = filtered.slice((current - 1) * PAGE_SIZE, current * PAGE_SIZE)

  const noFilters = !state.search && Object.keys(state.filters).length === 0
  const featured =
    current === 1 && noFilters ? (
      <ContentCard
        featured
        title="Scenic Group — Partner of the Month"
        mediaLabel="Scenic & Emerald"
        badge={<Tag variant="promo">Partner of the Month</Tag>}
        description="Turn your Scenic and Emerald bookings into an unforgettable reward."
      />
    ) : null

  return (
    <DataList<Partner>
      filters={filterDefs}
      filterState={state}
      onFilterChange={setFilterState}
      searchPlaceholder="Search..."
      items={pageItems}
      getKey={(p) => p.id}
      renderItem={(p) => (
        <ContentCard href="#" title={p.name} mediaLabel={p.name} description={p.tagline} />
      )}
      featured={featured}
      page={current}
      pageCount={pageCount}
      onPageChange={setPage}
      emptyTitle="No partners found"
      emptyDescription="Try adjusting your search or filters."
    />
  )
}
